import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import {
  Calendar,
  Building2,
  Clock,
  Users,
  Check,
  Star,
  ChevronRight,
  Ban,
  BarChart3,
  TrendingUp,
  CalendarDays,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
} from "lucide-react";

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="text-2xl font-medium text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{title}</p>
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="h-5 w-5 text-muted-foreground mb-2" />
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function StatusRow({ icon: Icon, label, value, color, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <Icon className={`h-4 w-4 ${color} shrink-0`} />
      <span className="text-sm flex-1">{label}</span>
      <span className="text-sm font-medium">{value}</span>
      <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
    </div>
  );
}

export default function OverviewPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, scorecardRes] = await Promise.all([
          axios.get(`${API}/owner/stats`).catch(() => ({ data: null })),
          axios.get(`${API}/owner/scorecard`).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setScorecard(scorecardRes.data);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/owner/scorecard/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `venue_report_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("owner_scorecard:download_success", { defaultValue: "Report downloaded!" }));
    } catch {
      toast.error(t("owner_scorecard:export_failed", { defaultValue: "Failed to export report" }));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!stats && !scorecard) {
    return (
      <div className="text-center py-12 bg-card rounded-xl border border-border">
        <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">{t("owner:no_venue_title")}</h3>
        <p className="text-muted-foreground">{t("owner:no_venue_help")}</p>
      </div>
    );
  }

  const scoreColor = scorecard && scorecard.overall_score >= 70
    ? "text-green-500"
    : scorecard && scorecard.overall_score >= 40
      ? "text-yellow-500"
      : "text-red-500";
  const scoreBg = scorecard && scorecard.overall_score >= 70
    ? "bg-green-500"
    : scorecard && scorecard.overall_score >= 40
      ? "bg-yellow-500"
      : "bg-red-500";

  return (
    <div data-testid="owner-overview-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h1 className="text-2xl font-medium text-foreground">{t("owner:welcome_back")}</h1>
        {scorecard && (
          <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? t("common:loading") : t("owner_scorecard:export_pdf")}
          </Button>
        )}
      </div>

      {/* Today KPI cards (from /owner/stats) */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard title={t("owner:stat_bookings_today")} value={stats.bookings_today} icon={Calendar} color="bg-blue-500" />
          <StatCard title={t("owner:stat_confirmed_today")} value={stats.confirmed_today} icon={Check} color="bg-green-500" />
          <StatCard title={t("owner:stat_pending")} value={stats.pending_bookings} icon={Clock} color="bg-yellow-500" />
          <StatCard title={t("owner:stat_guests")} value={stats.guests_today} icon={Users} color="bg-purple-500" />
        </div>
      )}

      {/* Overall performance score (from /owner/scorecard) */}
      {scorecard && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6" data-testid="scorecard-overall">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="relative w-24 h-24 shrink-0">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" className="text-muted" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" className={scoreColor} strokeWidth="8"
                  strokeDasharray={`${scorecard.overall_score * 2.64} 264`} strokeLinecap="round" />
              </svg>
              <span className={`absolute inset-0 flex items-center justify-center text-2xl font-bold ${scoreColor}`}>
                {scorecard.overall_score}
              </span>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-medium text-foreground">{scorecard.venue_name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("owner_scorecard:overall_description", { defaultValue: "Overall score based on bookings, reviews, and reliability" })}</p>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <span className="text-sm"><strong>{scorecard.bookings_30d}</strong> {t("owner_scorecard:bookings_30d", { defaultValue: "bookings (30d)" })}</span>
                <span className="text-sm"><strong>{scorecard.confirmation_rate}%</strong> {t("owner_scorecard:confirmed", { defaultValue: "confirmed" })}</span>
                {scorecard.avg_review_score && (
                  <span className="text-sm flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                    <strong>{scorecard.avg_review_score.toFixed(1)}</strong>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scorecard KPI tiles */}
      {scorecard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KPICard icon={CalendarDays} label={t("owner_scorecard:total_bookings", { defaultValue: "Total Bookings" })} value={scorecard.total_bookings} sub={t("owner_scorecard:all_time", { defaultValue: "all time" })} />
          <KPICard icon={TrendingUp} label={t("owner_scorecard:last_7_days", { defaultValue: "Last 7 Days" })} value={scorecard.bookings_7d} sub={t("owner_scorecard:bookings_sub", { defaultValue: "bookings" })} />
          <KPICard icon={Users} label={t("owner_scorecard:guests_30d", { defaultValue: "Guests (30d)" })} value={scorecard.total_guests_30d} sub={`${t("owner_scorecard:avg_prefix", { defaultValue: "avg" })} ${scorecard.avg_guests_per_booking}/${t("owner_scorecard:booking_short", { defaultValue: "booking" })}`} />
          <KPICard icon={Star} label={t("owner_scorecard:review_score", { defaultValue: "Review Score" })} value={scorecard.avg_review_score ? scorecard.avg_review_score.toFixed(1) : "N/A"} sub={`${scorecard.review_count} ${t("owner_scorecard:reviews", { defaultValue: "reviews" })}`} />
        </div>
      )}

      {/* Cancellation rate */}
      {stats && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6" data-testid="cancellation-rate-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("owner:cancellation_rate", { defaultValue: "Cancellation rate (this month)" })}
              </p>
              <div className="flex items-baseline gap-3 mt-2">
                <span
                  className={`text-3xl font-semibold ${
                    (stats.cancellation_rate || 0) >= 20
                      ? "text-red-600"
                      : (stats.cancellation_rate || 0) >= 10
                      ? "text-amber-600"
                      : "text-emerald-600"
                  }`}
                >
                  {(stats.cancellation_rate || 0).toFixed(1)}%
                </span>
                <span className="text-sm text-muted-foreground">
                  {stats.venue_cancellations_month || 0} / {stats.bookings_this_month || 0}{" "}
                  {t("owner:bookings_lowercase", { defaultValue: "bookings" })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {(stats.cancellation_rate || 0) <= 5
                  ? t("owner:cancellation_great")
                  : (stats.cancellation_rate || 0) < 15
                  ? t("owner:cancellation_ok")
                  : t("owner:cancellation_high")}
              </p>
            </div>
            <Ban
              className={`h-8 w-8 shrink-0 ${
                (stats.cancellation_rate || 0) >= 20
                  ? "text-red-400"
                  : (stats.cancellation_rate || 0) >= 10
                  ? "text-amber-400"
                  : "text-emerald-400"
              }`}
            />
          </div>
        </div>
      )}

      {/* Status breakdown + Peak hours */}
      {scorecard && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-card rounded-xl border border-border p-6" data-testid="scorecard-status-breakdown">
            <h3 className="font-medium text-foreground mb-4">{t("owner_scorecard:booking_status_30d", { defaultValue: "Booking Status (30 days)" })}</h3>
            <div className="space-y-3">
              <StatusRow icon={CheckCircle} label={t("owner_scorecard:status_confirmed_completed", { defaultValue: "Confirmed / Completed" })} value={scorecard.confirmed} color="text-green-500" total={scorecard.bookings_30d} />
              <StatusRow icon={XCircle} label={t("owner_scorecard:status_rejected", { defaultValue: "Rejected" })} value={scorecard.rejected} color="text-red-500" total={scorecard.bookings_30d} />
              <StatusRow icon={XCircle} label={t("owner_scorecard:status_canceled", { defaultValue: "Canceled" })} value={scorecard.canceled} color="text-gray-500" total={scorecard.bookings_30d} />
              <StatusRow icon={AlertTriangle} label={t("owner_scorecard:status_no_show", { defaultValue: "No Show" })} value={scorecard.no_show} color="text-orange-500" total={scorecard.bookings_30d} />
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6" data-testid="scorecard-peak-hours">
            <h3 className="font-medium text-foreground mb-4">{t("owner_scorecard:peak_times", { defaultValue: "Peak Booking Times" })}</h3>
            {scorecard.peak_hours && scorecard.peak_hours.length > 0 ? (
              <div className="space-y-3">
                {scorecard.peak_hours.map((ph, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium w-14">{ph.time}</span>
                    <div className="flex-1 bg-muted rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full ${scoreBg}`} style={{ width: `${Math.min((ph.bookings / (scorecard.peak_hours[0]?.bookings || 1)) * 100, 100)}%` }} />
                    </div>
                    <span className="text-sm text-muted-foreground w-8 text-right">{ph.bookings}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">{t("owner_scorecard:no_booking_data", { defaultValue: "No booking data yet" })}</p>
            )}
          </div>
        </div>
      )}

      {/* Daily trend */}
      {scorecard && scorecard.daily_trend && scorecard.daily_trend.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6" data-testid="scorecard-trend">
          <h3 className="font-medium text-foreground mb-4">{t("owner_scorecard:daily_bookings_14d", { defaultValue: "Daily Bookings (14 days)" })}</h3>
          <div className="flex items-end gap-1 h-32">
            {scorecard.daily_trend.map((d, i) => {
              const max = Math.max(...scorecard.daily_trend.map(x => x.bookings), 1);
              const h = Math.max((d.bookings / max) * 100, 4);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.bookings} bookings, ${d.guests} guests`}>
                  <span className="text-xs text-muted-foreground">{d.bookings}</span>
                  <div className={`w-full rounded-t ${scoreBg} transition-all`} style={{ height: `${h}%` }} />
                  <span className="text-[10px] text-muted-foreground">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent reviews */}
      {scorecard && scorecard.recent_reviews && scorecard.recent_reviews.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6" data-testid="scorecard-reviews">
          <h3 className="font-medium text-foreground mb-4">{t("owner_scorecard:recent_reviews", { defaultValue: "Recent Reviews" })}</h3>
          <div className="space-y-3">
            {scorecard.recent_reviews.map((r) => (
              <div key={r.review_id} className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{r.user_name}</span>
                  <div className="flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium">{r.score}/10</span>
                  </div>
                </div>
                {r.review_text && <p className="text-sm text-muted-foreground">{r.review_text}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review summary */}
      {stats && stats.review_score && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Star className="h-6 w-6 text-yellow-500 fill-yellow-500" />
              <span className="text-2xl font-medium">{stats.review_score.toFixed(1)}</span>
            </div>
            <span className="text-muted-foreground">
              {t("owner:based_on_reviews", { count: stats.review_count })}
            </span>
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/owner/bookings"
          className="bg-card rounded-xl border border-border p-6 hover:border-primary transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-foreground">{t("owner:manage_bookings")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("owner:manage_bookings_desc")}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </Link>

        <Link
          to="/owner/venue"
          className="bg-card rounded-xl border border-border p-6 hover:border-primary transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-foreground">{t("owner:edit_venue_profile")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("owner:edit_venue_profile_desc")}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </Link>
      </div>
    </div>
  );
}
